import type { TTranslationKeys } from "$/i18n/i18n"

import type { TEmptyCallback, TEmptyRenderCallback } from "@/shared/types/functions"

import type {
    TFeedbackModalAiSummaryMutation,
    TFeedbackModalChatMutation,
    TFeedbackModalInsightsMutation
} from "../api"

import { EFeedbackModalContext, EFeedbackModalSeverity } from "./feedback.modal.api.types"

enum EFeedbackModalFormField {
    Feedback = "text",
    Severity = "severity"
}

enum EFeedbackModalVariant {
    ChatGeneral = "general",
    ChatMessage = "message",
    ChatApiError = "apiError",
    ChatViolation = "chatViolation",
    SeedViolation = "seedViolation",
    SeedApiError = "seedApiError",
    Insights = "insights",
    AiGroupSummary = "aiGroupSummary",
    AiIndividualSummary = "aiIndividualSummary"
}

enum EFeedbackModalType {
    General = "general",
    GeneralExtended = "GeneralExtended",
    Report = "report"
}

type TFeedbackModalInputForm = {
    text: string
    severity: string
}

type TFeedbackModalMutationPayloadMapper = {
    [EFeedbackModalVariant.ChatGeneral]: {
        text: string
        context: EFeedbackModalContext.CoachingChatGeneral
    }
    [EFeedbackModalVariant.ChatMessage]: {
        text: string
        context: EFeedbackModalContext.CoachingChatMessage
        message_id: string
        severity: EFeedbackModalSeverity
    }
    [EFeedbackModalVariant.ChatApiError]: {
        text: string
        context: EFeedbackModalContext.CoachingChatApiError
        severity: EFeedbackModalSeverity
    }
    [EFeedbackModalVariant.ChatViolation]: {
        text: string
        context: EFeedbackModalContext.CoachingChatViolation
        moderation_id: number
    }
    [EFeedbackModalVariant.SeedViolation]: {
        text: string
        context: EFeedbackModalContext.CoachingChatViolation
        moderation_id: number
    }
    [EFeedbackModalVariant.SeedApiError]: {
        text: string
        context: EFeedbackModalContext.CoachingChatSeedError
    }
    [EFeedbackModalVariant.Insights]: {
        text: string
        chapter_progress: number
    }
    [EFeedbackModalVariant.AiGroupSummary]: {
        text: string
        context: EFeedbackModalContext.AiModuleSummary
        summary_id: number
    }
    [EFeedbackModalVariant.AiIndividualSummary]: {
        text: string
        context: EFeedbackModalContext.AiIndividualSummary
        summary_id: number
    }
}

type TFeedbackModalVariantMutationsMap = {
    [EFeedbackModalVariant.Insights]: TFeedbackModalInsightsMutation
    //
    [EFeedbackModalVariant.AiGroupSummary]: TFeedbackModalAiSummaryMutation
    [EFeedbackModalVariant.AiIndividualSummary]: TFeedbackModalAiSummaryMutation
    //
    [EFeedbackModalVariant.SeedViolation]: TFeedbackModalChatMutation
    [EFeedbackModalVariant.SeedApiError]: TFeedbackModalChatMutation
    [EFeedbackModalVariant.ChatMessage]: TFeedbackModalChatMutation
    [EFeedbackModalVariant.ChatApiError]: TFeedbackModalChatMutation
    [EFeedbackModalVariant.ChatViolation]: TFeedbackModalChatMutation
    [EFeedbackModalVariant.ChatGeneral]: TFeedbackModalChatMutation
}

type _TFeedbackModalInsightsProps = {
    variant: EFeedbackModalVariant.Insights
    payload: { chapterId: number }
}

type _TFeedbackModalAiSummaryProps = {
    variant: EFeedbackModalVariant.AiGroupSummary | EFeedbackModalVariant.AiIndividualSummary
    payload: { summaryId: number }
}

type _TFeedbackModalChatViolationProps = {
    variant: EFeedbackModalVariant.SeedViolation | EFeedbackModalVariant.ChatViolation
    payload: { moderationId: number }
}

type _TFeedbackModalChatGeneralProps = {
    variant: EFeedbackModalVariant.ChatGeneral
}

type _TFeedbackModalChatMessageProps = {
    variant: EFeedbackModalVariant.ChatMessage
    payload: { renderDetail: TEmptyRenderCallback; messageId: number }
    modalTitle: keyof TTranslationKeys
}

type _TFeedbackModalChatApiErrorProps = {
    variant: EFeedbackModalVariant.ChatApiError
    payload: { renderDetail: TEmptyRenderCallback }
    modalTitle: keyof TTranslationKeys
}

type _TFeedbackModalSeedApiErrorProps = {
    variant: EFeedbackModalVariant.SeedApiError
}

type TFeedbackModalProps = {
    type: EFeedbackModalType
} & (
    | _TFeedbackModalInsightsProps
    | _TFeedbackModalAiSummaryProps
    | _TFeedbackModalChatViolationProps
    | _TFeedbackModalChatGeneralProps
    | _TFeedbackModalChatMessageProps
    | _TFeedbackModalChatApiErrorProps
    | _TFeedbackModalSeedApiErrorProps
)

type TFeedbackModalBodyProps = {
    type: EFeedbackModalType
    renderDetail: TEmptyRenderCallback | undefined
    onClose: TEmptyCallback
    mutation: ReturnType<(variant: EFeedbackModalVariant) => TFeedbackModalVariantMutationsMap[EFeedbackModalVariant]>
    mutationParamsMapper: (
        formValues: TFeedbackModalInputForm
    ) => ReturnType<(variant: EFeedbackModalVariant) => TFeedbackModalMutationPayloadMapper[EFeedbackModalVariant]>
}

export { EFeedbackModalFormField, EFeedbackModalType, EFeedbackModalVariant }
export type {
    TFeedbackModalInputForm,
    TFeedbackModalProps,
    TFeedbackModalMutationPayloadMapper,
    TFeedbackModalVariantMutationsMap,
    TFeedbackModalBodyProps
}
